import { SortDirection } from '@angular/material/sort';
import { Action, Selector, State, StateContext, Store } from '@ngxs/store';
import {
    SetAllPostings,
    UnsetAllPostings,
    FetchAllPostings,
    FetchAllPostingsSuccess,
    FetchAllPostingsFailure,
    ToggleSelectedPosting,
    SelectFilteredPostings,
    DeselectSelectedPostings,
    SearchAllPostings,
    SortAllPostings,
    SortLanguageAllPostings,
    AddAllPostings,
} from '../actions/all-postings.action';
import { map } from 'rxjs/operators';
import { PostingApiService } from '../services/posting.api-service';
import { AllPostingsStateModel } from '../models/all-postings.state-model';
import { Posting } from '../models/posting.model';
import { PostingState } from './posting.state';
import { AddPostings } from '../actions';
import { Injectable } from '@angular/core';
import { SubentityState } from 'app/projects/subentity/src/lib/models/subentity.state';
import { UtilsState } from 'app/projects/subentity/src/lib/models/utils.state';

@State<AllPostingsStateModel>({
    name: 'allPostings',
    defaults: new AllPostingsStateModel(),
})
@Injectable()
export class AllPostingsState extends SubentityState<Posting> {
    @Selector([PostingState.getEntitiesAsMap])
    static getEntities(state: AllPostingsStateModel, postings: Map<string, Posting>): Posting[] {
        return UtilsState.getEntities(state, postings);
    }

    @Selector([PostingState.getEntities])
    static getFilteredEntities(state: AllPostingsStateModel, postings: Posting[]): Posting[] {
        if (!postings) {
            return null;
        }

        return postings
            .filter((item) => state.filtered.indexOf(item.id) !== -1)
            .map((item) => {
                return [state.list.indexOf(item.id), item];
            })
            .sort((a: [number, Posting], b: [number, Posting]) => {
                return a[0] > b[0] ? 1 : b[0] > a[0] ? -1 : 0;
            })
            .map((x: [number, Posting]) => x[1]);
    }

    @Selector()
    static getSearchTerm(state: AllPostingsStateModel): string {
        return state.searchTerm;
    }

    @Selector()
    static getTotalCount(state: AllPostingsStateModel): number {
        return state.totalCount;
    }

    @Selector()
    static isFetchingList(state: AllPostingsStateModel): boolean {
        return state.fetchingList;
    }

    @Selector()
    static getSortBy(state: AllPostingsStateModel): string {
        return state.sortBy;
    }

    @Selector()
    static getSortOrder(state: AllPostingsStateModel): SortDirection {
        return state.sortOrder;
    }

    @Selector()
    static getLanguage(state: AllPostingsStateModel): string {
        return state.language;
    }

    @Selector()
    static getPostingType(state: AllPostingsStateModel): number {
        return state.type;
    }

    @Selector()
    static getPageSize(state: AllPostingsStateModel): number {
        return state.pageSize || 10;
    }

    @Selector()
    static getPageNumber(state: AllPostingsStateModel): number {
        return state.pageNumber || 1;
    }

    @Selector()
    static getPageIndex(state: AllPostingsStateModel): number {
        return state.pageNumber ? state.pageNumber - 1 : 0;
    }

    constructor(private _postingApiService: PostingApiService, private store: Store) {
        super(null, 0);

        this._propstoSearch = ['posting', ...Posting.props_to_search];
    }

    @Action(SetAllPostings)
    setAll(ctx: StateContext<AllPostingsStateModel>, action: SetAllPostings): void {
        this._setSubentities(ctx, action, AddPostings);
    }

    @Action(AddAllPostings)
    addAll(ctx: StateContext<AllPostingsStateModel>, action: AddAllPostings): void {
        const postings = this.store.selectSnapshot(PostingState.getEntitiesAsMap);
        const allPostings = (ctx.getState().list || []).map((id) => postings.get(id));

        this._setSubentities(ctx, { payload: allPostings.concat(action.payload) }, AddPostings);
    }

    @Action(UnsetAllPostings)
    unsetAll(ctx: StateContext<AllPostingsStateModel>): void {
        UtilsState.search(ctx, '', AllPostingsStateModel.identifier);
        this._unsetSubentities(ctx);
    }

    @Action(FetchAllPostings)
    fetchAll(ctx: StateContext<AllPostingsStateModel>, { payload }: FetchAllPostings): void {
        this._fetchSubentities(
            ctx,
            { payload: 'all' },
            SetAllPostings,
            FetchAllPostingsSuccess,
            FetchAllPostingsFailure,
            (payload
                ? this._postingApiService.fetchPostings(
                      payload.sortBy,
                      payload.sortOrder,
                      payload.pageSize,
                      payload.pageNumber,
                      payload.search,
                      payload.postingTypeId,
                      payload.languageId,
                      payload.contentTypeId,
                      payload.onlyValid
                  )
                : this._postingApiService.fetchPostings()
            ).pipe(
                map((data) => {
                    ctx.patchState({
                        totalCount: data.pagination.number_of_results,
                    });

                    return data.data.map((query) => new Posting(query));
                })
            )
        );
    }

    @Action(FetchAllPostingsSuccess)
    fetchAllSuccess(ctx: StateContext<AllPostingsStateModel>): void {
        this._fetchSubentitiesSuccess(ctx);
    }

    @Action(FetchAllPostingsFailure)
    fetchAllFailure(ctx: StateContext<AllPostingsStateModel>): void {
        this._fetchSubentitiesFailure(ctx);
    }

    @Action(ToggleSelectedPosting)
    toggleSelect(ctx: StateContext<AllPostingsStateModel>, action: ToggleSelectedPosting): void {
        this._toggleSelectedSubentity(ctx, action);
    }

    @Action(SelectFilteredPostings)
    selectAll(ctx: StateContext<AllPostingsStateModel>): void {
        this._selectFilteredSubentities(ctx);
    }

    @Action(DeselectSelectedPostings)
    deselectAll(ctx: StateContext<AllPostingsStateModel>): void {
        this._deselectSelectedSubentities(ctx);
    }

    @Action(SearchAllPostings)
    search(ctx: StateContext<AllPostingsStateModel>, action: SearchAllPostings): void {
        UtilsState.search(ctx, action.payload, AllPostingsStateModel.identifier);
    }

    @Action(SortAllPostings)
    sort(stateContext: StateContext<AllPostingsStateModel>, { payload }: SortAllPostings): void {
        UtilsState.sort(stateContext, payload, AllPostingsStateModel.identifier);
    }

    @Action(SortLanguageAllPostings)
    sortLanguage(stateContext: StateContext<AllPostingsStateModel>, { payload }: SortLanguageAllPostings): void {
        UtilsState.language(stateContext, payload, AllPostingsStateModel.identifier);
    }
}
